import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, BehaviorSubject, combineLatest, Subject, EMPTY, timer } from 'rxjs';
import { startWith, map, tap, takeUntil, switchMap, pairwise, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { User, UserCheckSheetAssignmentProjection } from '@completion/models';
import { GetAssignmentsForUser, GetUsersWithOpenAssignments, UnlinkUsersAndCheckSheetAssignments } from '@completion/actions';
import { getCurrentProject, getUsersWithOpenScope, getSelectedUserScope, getResourceState } from '@completion/selectors';
import { CompletionStatus, ResourceStatus, ResourceType } from '@completion/enums';
import { UserAccessService } from '../../core/services';
import { ConfirmationService } from 'primeng/api';
import {
  buildProcurementPackageGroups,
  detectPackageFocusChange,
  findGroupAssignmentByTag,
  getAssignmentProgressPercent,
  getGroupProgressPercent,
  normalizeCompletionStatus,
  ProcurementPackageGroup,
} from '../supplier-scope.utils';

type UserProcurementPackageGroup = ProcurementPackageGroup<UserCheckSheetAssignmentProjection>;

@Component({
  selector: 'app-user-view',
  templateUrl: './user-view.component.html',
  standalone: false,
  styleUrls: ['./user-view.component.scss'],
})
export class UserViewComponent implements OnInit, OnDestroy {
  users$: Observable<User[]>;
  filter = new FormControl('');
  filteredUsers$: Observable<User[]>;
  selectedUser$ = new BehaviorSubject<User | null>(null);
  private selectedUserId$ = new BehaviorSubject<number | null>(null);

  // New: scope search for tag number
  scopeSearch = new FormControl('');

  // Auto-refresh toggle (every 10 seconds when enabled)
  private autoRefresh$ = new BehaviorSubject<boolean>(false);
  autoRefreshing$ = this.autoRefresh$.asObservable();

  selectedScopes$: Observable<UserCheckSheetAssignmentProjection[]> = this.store
    .select(getSelectedUserScope)
    .pipe(map((payload: any) => this.normalizeAssignments(payload)));

  // trigger to recompute groups so expansion state is preserved across updates
  private refresh$ = new BehaviorSubject<number>(0);
  private expandedMap = new Map<string, boolean>();

  groups$: Observable<UserProcurementPackageGroup[]> = combineLatest([this.selectedScopes$, this.refresh$]).pipe(
    map(([assignments]) => buildProcurementPackageGroups(assignments, this.expandedMap)),
  );

  // Keep last emitted groups for quick search
  private latestGroups: UserProcurementPackageGroup[] = [];

  // Assignment highlight state
  highlightedAssignmentId: number | null = null;

  // Animate auto-refresh icon once per fetch
  spinAnimating = false;

  // Unlink UI state per procurement package
  unlinkFilterByPackage = new Map<string, FormControl>();
  unlinkVisibleByPackage = new Map<string, boolean>();
  // store full assignment projections instead of just IDs
  unlinkSelectionByPackage = new Map<string, Set<UserCheckSheetAssignmentProjection>>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store<any>,
    private accee: UserAccessService,
    private confirmationService: ConfirmationService,
  ) {}

  // Convert a raw status/state value into a CompletionStatus enum value
  toCompletionStatus(value: unknown): CompletionStatus {
    return normalizeCompletionStatus(value as string | null | undefined);
  }

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((p: any) => {
        if (p && p.id) this.store.dispatch(new GetUsersWithOpenAssignments());
      });

    // Monitor unlink resource; when it succeeds, reload users and current user's assignments
    this.store
      .pipe(select(getResourceState, ResourceType.UnlinkUserCheckSheetAssignments), takeUntil(this.destroy$))
      .subscribe((resource) => {
        if (resource && resource.status === ResourceStatus.Success) {
          // Reload list of users with open assignments
          this.store.dispatch(new GetUsersWithOpenAssignments());
          // Reload assignments for currently selected user, if any
          const userId = this.selectedUserId$.value;
          if (userId != null) {
            this.store.dispatch(new GetAssignmentsForUser(userId));
          }
        }
      });

    this.users$ = this.store.select(getUsersWithOpenScope).pipe(
      tap((u) => console.debug('[UserView] users emitted', u)),
      map((u) => u || []),
      startWith([] as User[]),
    );

    this.filteredUsers$ = combineLatest([this.users$, this.filter.valueChanges.pipe(startWith(''))]).pipe(
      map(([users, q]) => {
        const query = (q || '').toLowerCase().trim();
        if (!query) return users;
        return users.filter((u) => (u.userName || '').toLowerCase().includes(query) || (u.name || '').toLowerCase().includes(query));
      }),
    );

    // Track groups for search and initialise unlink filters per package
    this.groups$.pipe(takeUntil(this.destroy$)).subscribe((gs) => {
      this.latestGroups = gs;
      gs.forEach((g) => {
        if (!this.unlinkFilterByPackage.has(g.packageKey)) {
          this.unlinkFilterByPackage.set(g.packageKey, new FormControl(''));
        }
      });
    });

    // React to data changes while auto-refresh is ON: open package and highlight if progress increased
    this.groups$
      .pipe(pairwise(), withLatestFrom(this.autoRefresh$), takeUntil(this.destroy$))
      .subscribe(([[prevGroups, nextGroups], enabled]) => {
        if (!enabled) return;
        this.detectAndFocusChanges(prevGroups, nextGroups);
      });

    // Auto-refresh the selected user's scope immediately and then every 10s when enabled
    combineLatest([this.autoRefresh$, this.selectedUserId$])
      .pipe(
        switchMap(([enabled, userId]) => (enabled && userId != null ? timer(0, 10000).pipe(map(() => userId)) : EMPTY)),
        takeUntil(this.destroy$),
      )
      .subscribe((userId) => {
        // Spin the icon once per fetch
        this.spinAnimating = true;
        setTimeout(() => (this.spinAnimating = false), 800);
        this.store.dispatch(new GetAssignmentsForUser(userId as number));
      });
  }

  get canUnlinkScoped() {
    return this.accee.canAccessSupplierModule;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectUser(user: User): void {
    // Stop any ongoing auto-refresh before switching users
    this.autoRefresh$.next(false);
    this.spinAnimating = false;

    this.selectedUser$.next(user);
    this.selectedUserId$.next(user.id);

    // Reset expansion and highlight state when selecting a new user
    this.expandedMap.clear();
    this.highlightedAssignmentId = null;
    this.refresh$.next(Date.now());
    this.store.dispatch(new GetAssignmentsForUser(user.id));

    // Clear unlink state when selecting new user
    this.unlinkVisibleByPackage.clear();
    this.unlinkSelectionByPackage.clear();
    this.unlinkFilterByPackage.clear();
  }

  toggleGroup(packageKey: string): void {
    this.expandedMap.set(packageKey, !this.expandedMap.get(packageKey));
    this.refresh$.next(Date.now());
  }

  // Search for tag number, expand the matching group and highlight the assignment
  findTag(): void {
    const q = (this.scopeSearch.value || '').toString().trim().toLowerCase();
    this.highlightedAssignmentId = null;
    if (!q) {
      this.refresh$.next(Date.now());
      return;
    }
    const match = findGroupAssignmentByTag(this.latestGroups, q);
    if (match) {
      this.expandedMap.set(match.group.packageKey, true);
      this.highlightedAssignmentId = match.assignment.id ?? null;
      this.refresh$.next(Date.now());
      // Allow DOM to update, then scroll to the highlighted row
      setTimeout(() => {
        const el = document.getElementById(`assignment-${match.assignment.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    }
  }

  clearHighlight(): void {
    this.highlightedAssignmentId = null;
    this.refresh$.next(Date.now());
  }

  // Package-level progress based on assignment-level progress values.
  // Assumption: assignment.progress is 0..1 representing fraction of completed check items per assignment.
  // We aggregate by simple mean due to lack of per-assignment item counts.
  getGroupProgress(g: UserProcurementPackageGroup): number {
    return getGroupProgressPercent(g);
  }

  // attempt to find a numeric progress value on the assignment (0..100)
  getProgress(a: UserCheckSheetAssignmentProjection): number {
    return getAssignmentProgressPercent(a);
  }

  toggleAutoRefresh(): void {
    this.autoRefresh$.next(!this.autoRefresh$.value);
  }

  // --- Unlink helpers ---

  toggleUnlinkPanel(packageKey: string): void {
    const cur = this.unlinkVisibleByPackage.get(packageKey) ?? false;
    this.unlinkVisibleByPackage.set(packageKey, !cur);
  }

  isUnlinkPanelVisible(packageKey: string): boolean {
    return this.unlinkVisibleByPackage.get(packageKey) ?? false;
  }

  getUnlinkFilterControl(packageKey: string): FormControl {
    let c = this.unlinkFilterByPackage.get(packageKey);
    if (!c) {
      c = new FormControl('');
      this.unlinkFilterByPackage.set(packageKey, c);
    }
    return c;
  }

  private getSelectionSetForPackage(packageKey: string): Set<UserCheckSheetAssignmentProjection> {
    let set = this.unlinkSelectionByPackage.get(packageKey);
    if (!set) {
      set = new Set<UserCheckSheetAssignmentProjection>();
      this.unlinkSelectionByPackage.set(packageKey, set);
    }
    return set;
  }

  isAssignmentSelectedForUnlink(packageKey: string, assignmentId: number): boolean {
    const set = this.getSelectionSetForPackage(packageKey);
    return Array.from(set).some((a) => a.id === assignmentId);
  }

  toggleAssignmentSelectionForUnlink(packageKey: string, assignmentId: number): void {
    const set = this.getSelectionSetForPackage(packageKey);
    // find the full projection for this id in the latest group for the package
    const group = this.latestGroups.find((g) => g.packageKey === packageKey);
    if (!group) {
      return;
    }
    const assignment = (group.assignments || []).find((a) => a.id === assignmentId);
    if (!assignment) {
      return;
    }

    const existing = Array.from(set).find((a) => a.id === assignmentId);
    if (existing) {
      set.delete(existing);
    } else {
      set.add(assignment);
    }
  }

  isUnlinkDisabled(packageKey: string): boolean {
    const set = this.getSelectionSetForPackage(packageKey);
    return set.size === 0;
  }

  openUnlinkConfirmation(group: UserProcurementPackageGroup): void {
    const selection = Array.from(this.getSelectionSetForPackage(group.packageKey).values());
    if (!selection.length) {
      return;
    }

    const count = selection.length;
    const packageName = group.packageNumber || 'Unknown package';

    this.confirmationService.confirm({
      header: 'Unlink checksheets',
      message:
        count === 1
          ? `Are you sure you want to unlink 1 checksheet from package ${packageName}?`
          : `Are you sure you want to unlink ${count} checksheets from package ${packageName}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonProps: {
        label: 'Unlink',
        severity: 'danger',
      },
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      accept: () => {
        this.unlinkSelected(group.packageKey);
      },
    });
  }

  // unlinkSelected remains as the actual unlink operation
  unlinkSelected(packageKey: string): void {
    const selection = Array.from(this.getSelectionSetForPackage(packageKey).values());
    if (!selection.length) {
      return;
    }

    // Dispatch projections directly so backend gets full CheckSheetAssignmentProjection objects
    this.store.dispatch(new UnlinkUsersAndCheckSheetAssignments(selection));

    // Clear selection after unlink
    this.getSelectionSetForPackage(packageKey).clear();
  }

  private detectAndFocusChanges(prevGroups: UserProcurementPackageGroup[], nextGroups: UserProcurementPackageGroup[]): void {
    const focus = detectPackageFocusChange(prevGroups, nextGroups, (assignment) =>
      this.toCompletionStatus((assignment as any).status ?? (assignment as any).state),
    );
    if (focus) {
      this.expandedMap.set(focus.packageKey, true);
      this.highlightedAssignmentId = focus.id;
      this.refresh$.next(Date.now());
      setTimeout(() => {
        const el = document.getElementById(`assignment-${focus!.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }

  protected areAllSelectedForUnlink(packageKey: string): boolean {
    const group = this.latestGroups.find((g) => g.packageKey === packageKey);
    if (!group) {
      return false;
    }
    const filtered = this.getFilteredAssignmentsForUnlink(group);
    if (!filtered.length) {
      return false;
    }
    const set = this.getSelectionSetForPackage(packageKey);
    // All visible assignments must be in the selection set
    return filtered.every((a) => Array.from(set).some((sel) => sel.id === a.id));
  }

  protected toggleAllSelectionsForUnlink(packageKey: string): void {
    const group = this.latestGroups.find((g) => g.packageKey === packageKey);
    if (!group) {
      return;
    }
    const filtered = this.getFilteredAssignmentsForUnlink(group);
    const set = this.getSelectionSetForPackage(packageKey);

    const allSelected = filtered.length > 0 && filtered.every((a) => Array.from(set).some((sel) => sel.id === a.id));
    if (allSelected) {
      // Clear only the currently visible ones from the selection
      filtered.forEach((a) => {
        const existing = Array.from(set).find((sel) => sel.id === a.id);
        if (existing) {
          set.delete(existing);
        }
      });
    } else {
      // Add all currently visible ones to the selection
      filtered.forEach((a) => {
        const existing = Array.from(set).find((sel) => sel.id === a.id);
        if (!existing) {
          set.add(a);
        }
      });
    }
  }

  getFilteredAssignmentsForUnlink(g: UserProcurementPackageGroup): UserCheckSheetAssignmentProjection[] {
    const control = this.getUnlinkFilterControl(g.packageKey);
    const query = (control.value || '').toString().trim().toLowerCase();
    if (!query) {
      return g.assignments || [];
    }

    return (g.assignments || []).filter((a) => {
      const tag = (a.tagNumber || '').toLowerCase();
      const checkSheet = (a.checkSheetNumber || (a as any).checkSheet || '').toLowerCase();
      const company = (a.companyNumber || (a as any).company || '').toLowerCase();
      const site = (a.siteNumber || (a as any).mcSite || (a as any).site || '').toLowerCase();
      const phase = (a.phaseNumber || (a as any).phase || '').toLowerCase();
      const system = (a.systemNumber || '').toLowerCase();

      return (
        tag.includes(query) ||
        checkSheet.includes(query) ||
        company.includes(query) ||
        site.includes(query) ||
        phase.includes(query) ||
        system.includes(query)
      );
    });
  }

  private normalizeAssignments(payload: unknown): UserCheckSheetAssignmentProjection[] {
    if (!payload) {
      return [];
    }

    if (Array.isArray(payload)) {
      return payload as UserCheckSheetAssignmentProjection[];
    }

    if (typeof payload === 'object') {
      return Object.values(payload as Record<string, UserCheckSheetAssignmentProjection[]>)
        .flat()
        .filter((assignment): assignment is UserCheckSheetAssignmentProjection => assignment != null);
    }

    return [];
  }
}
